import type { ReactNode } from 'react';

/**
 * An on/off switch with its label and explanation.
 *
 * The switch is the whole control: the label is a `<label>`-shaped clickable
 * region rather than decoration beside a 44px target, because these appear in
 * settings lists where the sentence is what people aim at.
 *
 * The hand-rolled version this replaces had no visible focus state, so the
 * control was unreachable-looking for anyone driving the page from a keyboard.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{label}</p>
        {description && <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{description}</p>}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition duration-200 ease-out
          focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600
          disabled:cursor-not-allowed disabled:opacity-50
          ${checked ? 'bg-brand-600' : 'bg-slate-300'}`}
      >
        <span
          aria-hidden="true"
          className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-all duration-200 ease-out
            ${checked ? 'left-5.5' : 'left-0.5'}`}
        />
      </button>
    </div>
  );
}

/**
 * A choice between named options, as one row of segments.
 *
 * Used where a boolean would lose information — "how much of this do you want
 * to see" is not a yes/no question, and a switch would force it to be one.
 */
export function SegmentedChoice<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled = false,
}: {
  value: T;
  options: { value: T; label: string; description?: string }[];
  onChange: (next: T) => void;
  label: string;
  disabled?: boolean;
}) {
  const active = options.find((option) => option.value === value);

  return (
    <div className="py-3">
      <p className="text-sm font-semibold text-ink">{label}</p>

      <div
        role="radiogroup"
        aria-label={label}
        className="mt-2 inline-flex rounded-xl border border-line bg-canvas p-1"
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition duration-200 ease-out
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600
                disabled:cursor-not-allowed disabled:opacity-50
                ${selected ? 'bg-white text-ink shadow-soft' : 'text-slate-500 hover:text-ink'}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {/* One explanation, for the option actually chosen — a list of all of
          them makes the reader do the matching themselves. */}
      {active?.description && (
        <p className="mt-2 text-xs leading-relaxed text-slate-500">{active.description}</p>
      )}
    </div>
  );
}
